import React, { useMemo, useEffect, useCallback } from 'react';
import { IsJsonString } from 'helpers';
import { isArray, isPlainObject } from 'lodash';
import ColumnMonitoringModel, { enumColumn, enumColumnsV2 } from 'models/column-monitoring.model';
import getUser from 'redux/selector/getUser';
import { useDispatch } from 'react-redux';
import actions from 'redux/actions';

export const useColumnsHandler = () => React.useContext(ColumnsHandlerContext);

const checkFieldAvailable = ({ data = [], field = 'value', value = '' }) => {
  return !!data.find((el) => el?.[field] === value);
};

const defaultColumns = new ColumnMonitoringModel().columns;

const { saveLayoutMonitoring, saveLayoutContactGroup } = actions;

const ColumnsHandlerContext = React.createContext({
  contactGroupLayout: [],
  monitoringV1Layout: [],
  monitoringV2Layout: [],
  /**
   *
   * @param {*} nextColumns
   * @param {Object} options
   * @param {"monitoring-v1" | "monitoring-v2" | "contact-group"} options.type
   * @param {() => void} options.onSuccess
   * @param {(error) => void} options.onFailed
   */
  save: (nextColumns, { type, onSuccess, onFailed }) => {},

  /**
   * Sync stats columns with monitoring v2
   * @param {Array} columns
   * @returns {Array}
   */
  orderingStatsColumnsByRootLayoutMonitoring: (columns) => {},
});

const ColumnsHandlerProvider = ({ children }) => {
  const dispatch = useDispatch();
  const userInfo = getUser();
  const campaignMonitoringLayout = userInfo?.settings?.campaignMonitoringLayout || '';

  console.log('userInfo', userInfo);

  //! Fix bug for some reason, one column is removed
  useEffect(() => {
    if (!userInfo.isLogged) {
      return;
    }

    if (campaignMonitoringLayout && IsJsonString(campaignMonitoringLayout)) {
      const parsedCampaignMonitoringLayout = JSON.parse(campaignMonitoringLayout);

      if (isArray(parsedCampaignMonitoringLayout)) {
        //! For migrating response data from old version -> new version
        const layouts = {
          v1: parsedCampaignMonitoringLayout,
          v2: [],
        };

        dispatch(saveLayoutMonitoring({ nextColumns: layouts }));
        return;
      } else if (isPlainObject(parsedCampaignMonitoringLayout)) {
        const layouts = {
          v1: parsedCampaignMonitoringLayout?.v1,
          v2: parsedCampaignMonitoringLayout?.v2,
        };
        let needUpdate = false;

        const handleColumnsV1 = () => {
          const layoutV1 = layouts?.v1 || [];
          //* Fix bug TA-10624 - https://textback.atlassian.net/browse/TA-10642
          const fieldNeedCheck = [
            enumColumn.progress,
            enumColumn.timeRemaining,
            enumColumn.speed,
            enumColumn.profit,
            enumColumn.activeCallCount,
            enumColumn.ROI,
            enumColumn.revenuePerMilMinute,
            enumColumn.revenuePerMilMessage,
            enumColumn.revenuePerCalls,
            enumColumn.callRate,
          ];

          fieldNeedCheck.forEach((field) => {
            const isFieldNotFound = !checkFieldAvailable({
              data: layoutV1,
              value: field,
            });

            if (isFieldNotFound) {
              needUpdate = true;

              const temp = {
                column: defaultColumns?.find((el) => el.value === field)?.column,
                value: field,
                isChecked: true,
                isSortable: true,
                width: '200px',
              };

              layoutV1.push(temp);
            }
          });

          //* Check group cost
          const costGrouped = layoutV1.find((el) => el.value === enumColumn.cost);
          const fieldInCostNeedCheck = [enumColumn.amdCost];

          if (costGrouped) {
            fieldInCostNeedCheck.forEach((field) => {
              const isFieldNotFound = !costGrouped?.subColumn?.find((col) => col.value === field);

              if (isFieldNotFound) {
                needUpdate = true;
                const defaultCostGrouped = defaultColumns?.find(
                  (el) => el.value === enumColumn.cost,
                );

                const temp = {
                  column: defaultCostGrouped?.subColumn?.find((el) => el.value === field)?.column,
                  value: field,
                  isChecked: true,
                  isChild: true,
                  isSortable: true,
                  width: '140px',
                };

                costGrouped.subColumn.push(temp);
              }
            });
          }

          //* Add callbackCount to statistic
          const statistic = layoutV1.find((el) => el.value === enumColumn.statistic);
          const fieldInStatisticsNeedCheck = [
            enumColumn.callbackCount,
            enumColumn.listenCounter,
            enumColumn.callDurationInMinute,
            enumColumn.callDurationInSecond,
            enumColumn.ivrClickRate,
            enumColumn.ivrOptoutRate,
            enumColumn.amd,
            enumColumn.rejected,
          ];

          if (statistic) {
            fieldInStatisticsNeedCheck.forEach((field) => {
              const isFieldNotFound = !statistic?.subColumn?.find((col) => col.value === field);

              if (isFieldNotFound) {
                needUpdate = true;
                const defaultStatistic = defaultColumns?.find(
                  (el) => el.value === enumColumn.statistic,
                );

                const temp = {
                  column: defaultStatistic?.subColumn?.find((el) => el.value === field)?.column,
                  value: field,
                  isChecked: true,
                  isChild: true,
                  isSortable: true,
                  width: '80px',
                };

                statistic.subColumn.push(temp);
              }
            });

            //* Disabled statistic sort -> Server is no longer support sort by stats
            if (statistic?.subColumn?.some((el) => el?.isSortable)) {
              needUpdate = true;
              statistic?.subColumn?.forEach((el) => {
                el.isSortable = false;
              });
            }
          }
        };

        const handleColumnsV2 = () => {
          //* Sync column from v1 to v2
          const needMigrate = layouts?.v2?.some(
            (el) => el?.value === 'acds' || el?.value === 'optoutRate',
          );
          if (needMigrate) {
            const newV2Columns = layouts?.v2?.map((el) => {
              const mappingOldToNew = {
                optoutRate: enumColumnsV2.ivrOptoutRate,
                Clicked: enumColumnsV2.clicked,
                Replied: enumColumnsV2.replied,
                Callback: enumColumnsV2.callback,
                'Call blocks': enumColumnsV2.callblocks,
                acds: enumColumnsV2.acd,
              };

              return {
                ...el,
                value: mappingOldToNew[el?.value] || el?.value,
              };
            });

            needUpdate = true;
            layouts.v2 = newV2Columns;
          }
        };

        handleColumnsV1();
        handleColumnsV2();

        if (needUpdate) {
          dispatch(saveLayoutMonitoring({ nextColumns: layouts }));
        }
      }
    }
  }, [userInfo.isLogged, campaignMonitoringLayout, dispatch]);

  //! Function
  const save = useCallback(
    (nextColumns, { type, onSuccess, onFailed } = {}) => {
      if (type === 'contact-group') {
        dispatch(
          saveLayoutContactGroup({ nextColumns: nextColumns, callbacks: { onSuccess, onFailed } }),
        );
        return;
      }

      const layouts = {
        v1: userInfo?.monitoringV1Layout,
        v2: userInfo?.monitoringV2Layout,
      };

      if (type === 'monitoring-v1') {
        layouts.v1 = nextColumns;
        dispatch(
          saveLayoutMonitoring({ nextColumns: layouts, callbacks: { onSuccess, onFailed } }),
        );
      }

      if (type === 'monitoring-v2') {
        layouts.v2 = nextColumns.filter(el => !!el.label);
        dispatch(
          saveLayoutMonitoring({ nextColumns: layouts, callbacks: { onSuccess, onFailed } }),
        );
      }
    },
    [userInfo],
  );

  const orderingStatsColumnsByRootLayoutMonitoring = useCallback(
    (columns) => {
      //* Ordering stats columns by root layout monitoring
      const currentOrderingColumns = columns.map((el) => el.id) || [];
      const rootOrderingColumns =
        userInfo?.monitoringV2Layout
          ?.map((el) => el.value)
          .filter((col) => currentOrderingColumns.includes(col)) || [];

      const orderedSet = new Set(rootOrderingColumns);
      console.log('orderedSet', {currentOrderingColumns, rootOrderingColumns, orderedSet, mapped:         userInfo?.monitoringV2Layout
        ?.map((el) => el.value), layout: userInfo?.monitoringV2Layout})

      let pointer = 0;
      const result =
        currentOrderingColumns
          .map((item) => {
            if (!orderedSet.has(item)) {
              return item; // keep original position
            }

            return rootOrderingColumns[pointer++]; // put next ordered item
          })
          .map((col) => {
            const columnExistInRootLayout = userInfo?.monitoringV2Layout?.find(
              (el) => el.value === col,
            );
            const isHide = columnExistInRootLayout?.isHide || false;

            return { value: col, isHide };
          }) || [];

      return result;
    },
    [userInfo?.monitoringV2Layout],
  );

  const value = useMemo(() => {
    return {
      save,
      contactGroupLayout: userInfo?.layoutContact,
      monitoringV1Layout: userInfo?.monitoringV1Layout,
      monitoringV2Layout: userInfo?.monitoringV2Layout,
      orderingStatsColumnsByRootLayoutMonitoring,
    };
  }, [userInfo, save, orderingStatsColumnsByRootLayoutMonitoring]);

  return <ColumnsHandlerContext.Provider value={value}>{children}</ColumnsHandlerContext.Provider>;
};

export default ColumnsHandlerProvider;
